import { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
    Menu,
    X,
    ArrowUpRight,
    Phone,
    Mail,
    MapPin,
    Linkedin,
    Search,
    Package,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const links = [
    { to: "/", label: "Home" },
    { to: "/services", label: "Services" },
    { to: "/about", label: "About" },
    { to: "/blog", label: "News" },
    { to: "/contact", label: "Contact" },
];

const LOGO_SRC = "/assets/beeta-logo-transparent.webp";

export default function Navbar() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [sideTrackId, setSideTrackId] = useState("");
    const navigate = useNavigate();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 10);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    // Lock body scroll when sidebar open
    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    const closeSidebar = () => setOpen(false);

    const submitSideTrack = (e) => {
        e.preventDefault();
        const id = sideTrackId.trim().toUpperCase();
        if (!id) return;
        navigate(`/track?waybill=${encodeURIComponent(id)}`);
        setSideTrackId("");
        closeSidebar();
    };

    return (
        <>
            <header
                data-testid="global-navbar"
                className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
                    scrolled
                        ? "bg-white/85 backdrop-blur-xl border-b border-slate-200/60 shadow-sm"
                        : "bg-white/60 backdrop-blur-md"
                }`}
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-20">
                        <Link
                            to="/"
                            data-testid="navbar-logo-link"
                            className="flex items-center gap-2.5 group"
                        >
                            <img
                                src={LOGO_SRC}
                                alt="Beeta Logistics"
                                className="h-10 sm:h-11 w-auto transition-transform group-hover:-translate-y-0.5"
                            />
                        </Link>

                        <nav className="hidden lg:flex items-center gap-1">
                            {links.map((l) => (
                                <NavLink
                                    key={l.to}
                                    to={l.to}
                                    data-testid={`nav-link-${l.label.toLowerCase()}`}
                                    className={({ isActive }) =>
                                        `relative px-4 py-2 text-sm font-semibold rounded-full transition-colors ${
                                            isActive
                                                ? "text-blue-600"
                                                : "text-slate-700 hover:text-blue-600 hover:bg-slate-50"
                                        }`
                                    }
                                    end={l.to === "/"}
                                >
                                    {({ isActive }) => (
                                        <>
                                            {l.label}
                                            {isActive && (
                                                <motion.span
                                                    layoutId="nav-active-underline"
                                                    className="absolute left-4 right-4 -bottom-0.5 h-[3px] rounded-full bg-brand-yellow"
                                                    transition={{ type: "spring", stiffness: 380, damping: 30 }}
                                                />
                                            )}
                                        </>
                                    )}
                                </NavLink>
                            ))}
                        </nav>

                        <div className="hidden lg:flex items-center gap-3">
                            <Link
                                to="/track"
                                data-testid="navbar-track-cta"
                                className="group inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-colors shadow-sm"
                            >
                                <Package className="w-4 h-4" />
                                Track Shipment
                                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                            </Link>
                        </div>

                        <button
                            data-testid="navbar-mobile-toggle"
                            onClick={() => setOpen(true)}
                            className="lg:hidden w-11 h-11 rounded-full bg-slate-900 text-white flex items-center justify-center hover:bg-blue-600 transition-colors"
                            aria-label="Open menu"
                            aria-expanded={open}
                            aria-controls="mobile-sidebar"
                        >
                            <Menu className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </header>

            {/* Sidebar Drawer */}
            <AnimatePresence>
                {open && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.25 }}
                            onClick={() => setOpen(false)}
                            className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 lg:hidden"
                            data-testid="sidebar-overlay"
                            aria-hidden="true"
                        />
                        <motion.aside
                            id="mobile-sidebar"
                            data-testid="sidebar-drawer"
                            initial={{ x: "100%" }}
                            animate={{ x: 0 }}
                            exit={{ x: "100%" }}
                            transition={{ type: "tween", ease: [0.22, 1, 0.36, 1], duration: 0.35 }}
                            className="fixed top-0 right-0 bottom-0 w-[88%] max-w-sm bg-white z-50 lg:hidden shadow-2xl flex flex-col"
                            role="dialog"
                            aria-modal="true"
                            aria-label="Mobile navigation"
                        >
                            {/* Header */}
                            <div className="flex items-center justify-between px-6 h-20 border-b border-slate-200">
                                <img
                                    src={LOGO_SRC}
                                    alt="Beeta Logistics"
                                    className="h-9 w-auto"
                                />
                                <button
                                    data-testid="sidebar-close"
                                    onClick={() => setOpen(false)}
                                    className="w-10 h-10 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-900 transition-colors"
                                    aria-label="Close menu"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            {/* Nav Links */}
                            <nav className="flex-1 overflow-y-auto px-4 py-6">
                                {/* Quick track */}
                                <div className="mb-6 px-2">
                                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 px-2 mb-3">
                                        Track a shipment
                                    </div>
                                    <form
                                        onSubmit={submitSideTrack}
                                        data-testid="sidebar-track-form"
                                        className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 focus-within:bg-white focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100 px-3 py-2 transition-all"
                                    >
                                        <Search className="w-4 h-4 text-slate-500 shrink-0" />
                                        <input
                                            data-testid="sidebar-track-input"
                                            type="text"
                                            value={sideTrackId}
                                            onChange={(e) => setSideTrackId(e.target.value)}
                                            placeholder="Enter waybill number"
                                            aria-label="Track waybill number"
                                            className="flex-1 min-w-0 bg-transparent text-sm text-slate-900 placeholder-slate-500 outline-none py-1.5"
                                        />
                                        <button
                                            type="submit"
                                            data-testid="sidebar-track-submit"
                                            aria-label="Track shipment"
                                            className="w-8 h-8 rounded-lg bg-slate-900 text-white flex items-center justify-center hover:bg-blue-600 transition-colors shrink-0"
                                        >
                                            <ArrowUpRight className="w-4 h-4" />
                                        </button>
                                    </form>
                                </div>

                                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 px-4 mb-3">
                                    Navigate
                                </div>
                                <div className="space-y-1">
                                    {links.map((l, i) => (
                                        <motion.div
                                            key={l.to}
                                            initial={{ opacity: 0, x: 16 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: 0.1 + i * 0.04, duration: 0.3 }}
                                        >
                                            <NavLink
                                                to={l.to}
                                                data-testid={`sidebar-nav-${l.label.toLowerCase()}`}
                                                onClick={closeSidebar}
                                                className={({ isActive }) =>
                                                    `group flex items-center justify-between px-4 py-3.5 rounded-xl text-base font-semibold transition-colors ${
                                                        isActive
                                                            ? "bg-blue-600 text-white"
                                                            : "text-slate-800 hover:bg-slate-50"
                                                    }`
                                                }
                                                end={l.to === "/"}
                                            >
                                                {({ isActive }) => (
                                                    <>
                                                        <span>{l.label}</span>
                                                        <ArrowUpRight
                                                            className={`w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 ${
                                                                isActive ? "text-white" : "text-slate-400"
                                                            }`}
                                                        />
                                                    </>
                                                )}
                                            </NavLink>
                                        </motion.div>
                                    ))}
                                </div>

                                {/* Contact */}
                                <div className="mt-10 px-2">
                                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 px-2 mb-3">
                                        Reach Us
                                    </div>
                                    <ul className="space-y-3 text-sm text-slate-700">
                                        <li className="flex items-start gap-3 px-2">
                                            <Phone className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
                                            <a href="tel:+919958018358" className="hover:text-blue-600">
                                                +91 99580 18358
                                            </a>
                                        </li>
                                        <li className="flex items-start gap-3 px-2">
                                            <Mail className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
                                            <a href="mailto:info@beetalogistics.com" className="hover:text-blue-600 break-all">
                                                info@beetalogistics.com
                                            </a>
                                        </li>
                                        <li className="flex items-start gap-3 px-2">
                                            <MapPin className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
                                            <span>Sector 74-A, Gurgaon — India</span>
                                        </li>
                                    </ul>
                                </div>

                                {/* Socials */}
                                <div className="mt-10 px-2">
                                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 px-2 mb-3">
                                        Follow
                                    </div>
                                    <div className="flex gap-2 px-2">
                                        <a
                                            href="https://www.linkedin.com/in/chiraag-shridhar-30908152"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            data-testid="sidebar-social-linkedin"
                                            className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-slate-100 hover:bg-blue-600 hover:text-white text-slate-700 text-sm font-semibold transition-colors"
                                            aria-label="LinkedIn"
                                        >
                                            <Linkedin className="w-4 h-4" />
                                            LinkedIn
                                        </a>
                                    </div>
                                </div>
                            </nav>

                            {/* Footer tagline */}
                            <div className="p-4 border-t border-slate-200 bg-slate-50">
                                <div className="text-[11px] text-center text-slate-500 font-semibold uppercase tracking-[0.15em]">
                                    Transportation beyond compare
                                </div>
                            </div>
                        </motion.aside>
                    </>
                )}
            </AnimatePresence>
        </>
    );
}
